import React from 'react';
import styles from '@/styles/components/question-builders/base/Scale.module.css';
import DragList, { DragListProps } from '../../drag-and-drop/DragList';
import { removeById, fixOrder } from '@ax2/common/helpers/array';

import {
  TemplateContentElementData,
  TemplateContentElement,
} from './TemplateContentElement';
import UUID from '@ax2/common/model/UUID';
import { Card } from 'components/drag-and-drop/DragCard';
import { AddActionProps } from 'components/drag-and-drop/actions/AddAction';
import { RemoveActionProps } from 'components/drag-and-drop/actions/RemoveAction';
import radioButtonIcon from '../../../assets/radio_button_gray.svg';
import _ from 'lodash';

export interface StepOption extends TemplateContentElementData {
  id: UUID;
  order: number;
  label: string | null;
}
export interface AttrOption extends TemplateContentElementData {
  id: UUID;
  order: number;
}

export interface ScaleData {
  steps: StepOption[];
  attributes: AttrOption[];
  isLocked: boolean;
  isAttrsHidden: boolean;
  onNewStep: () => void;
  onNewAttr: () => void;
  onChangeSteps: (steps: StepOption[]) => void;
  onChangeAttrs: (attrs: AttrOption[]) => void;
}

const ItemControl = (data: StepOption | AttrOption) => {
  return (
    <div className={styles.templatedText}>
      <TemplateContentElement {...data} key={data.id} />
    </div>
  );
};
const LabelControl = (data: StepOption) => {
  return (
    <div className={styles.labelControl}>
      <span className={styles.labelText}>{data.label}</span>
    </div>
  );
};
const isSectionLocked = (
  isGlobalLocked: boolean,
  sectionOptions: (StepOption | AttrOption)[]
) =>
  isGlobalLocked &&
  sectionOptions.length > 0 &&
  _.findIndex(sectionOptions, so => so.templateType === 'template') === -1;

function getStepDragListProps(
  options: StepOption[],
  isLocked: boolean,
  onChange: (opt: StepOption[]) => void,
  addAction?: AddActionProps,
  removeAction?: RemoveActionProps<StepOption>
): DragListProps<StepOption> {
  const isSLocked = isSectionLocked(isLocked, options);
  const cards: Card<StepOption>[] = options.map(opt => ({
    itemControl: ItemControl(opt),
    orderControl: () => LabelControl(opt),
    dragZone: 'none',
    itemFrame: 'border',
    isEditable: opt.templateType === 'template',
    item: opt,
  }));
  return {
    cards: cards,
    limit: 11,
    addAction: isSLocked ? undefined : addAction,
    removeAction: isSLocked ? undefined : removeAction,
    onComplete: data => onChange(fixOrder(data.map(d => d.item))),
  };
}

function getAttributesDragListProps(
  options: AttrOption[],
  onChange: (opt: AttrOption[]) => void,
  isLocked: boolean,
  addAction?: AddActionProps,
  removeAction?: RemoveActionProps<AttrOption>
): DragListProps<AttrOption> {
  const isSLocked = isSectionLocked(isLocked, options);
  const cards: Card<AttrOption>[] = options.map(opt => ({
    itemControl: ItemControl(opt),
    dragZone: isSLocked ? 'none' : 'icon',
    itemFrame: 'border',
    typeIcon: radioButtonIcon,
    isEditable: opt.templateType === 'template',
    item: opt,
  }));
  return {
    cards: cards,
    limit: 5,
    addAction: isSLocked ? undefined : addAction,
    removeAction: isSLocked ? undefined : removeAction,
    onComplete: data => onChange(fixOrder(data.map(d => d.item))),
  };
}

export const Scale: React.FC<ScaleData> = props => {
  return (
    <div className={styles.gridContainer}>
      <div className={styles.scaleContainer}>
        <span className={styles.title}>Scale</span>
        <DragList
          {...getStepDragListProps(
            props.steps,
            props.isLocked,
            props.onChangeSteps,
            {
              onAdd: props.onNewStep,
            },
            {
              onRemove: item => {
                const steps = removeById(props.steps, item.id);
                props.onChangeSteps(fixOrder(steps));
              },
            }
          )}
        />
      </div>
      <div className={styles.attributesContainer}>
        {!props.isAttrsHidden && (
          <>
            <span className={styles.title}>Variables</span>
            <DragList
              {...getAttributesDragListProps(
                props.attributes,
                props.onChangeAttrs,
                props.isLocked,
                {
                  onAdd: props.onNewAttr,
                },
                {
                  onRemove: item => {
                    const attributes = removeById(props.attributes, item.id);
                    props.onChangeAttrs(fixOrder(attributes));
                  },
                }
              )}
            />
          </>
        )}
      </div>
    </div>
  );
};
